/**
 * BaseQuery dùng mock data cho POC. Khi triển khai thật: tắt VITE_USE_MOCK, API slice sẽ gọi backend thật.
 */
import type { BaseQueryFn } from '@reduxjs/toolkit/query'
import {
  mockCases,
  mockCaseDetails,
  mockFilings,
  mockDocuments,
  mockSessions,
  mockAlerts,
  mockJudges,
} from './data'
import type { Case, CaseDetail, CaseEvent } from '../types/case'
import type { Filing } from '../types/filing'
import type { Document } from '../types/document'

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))

function parseUrl(url: string): { path: string; id?: string; action?: string; query?: Record<string, string> } {
  const [pathPart, queryPart] = url.split('?')
  const clean = pathPart.replace(/^\/api\/v1/, '').replace(/^\//, '')
  const segments = clean.split('/').filter(Boolean)
  const path = segments[0] ?? ''
  const action = segments[segments.length - 1] === 'assign' ? 'assign' : undefined
  const id = segments.length >= 2 && segments[segments.length - 1] !== 'assign'
    ? segments[segments.length - 1]
    : segments.length >= 3 && segments[segments.length - 1] === 'assign'
      ? segments[segments.length - 2]
      : undefined
  const query: Record<string, string> = {}
  if (queryPart) {
    queryPart.split('&').forEach((p) => {
      const [k, v] = p.split('=')
      if (k && v) query[k] = decodeURIComponent(v)
    })
  }
  return { path, id, action, query }
}

export const mockBaseQuery: BaseQueryFn<
  { url: string; method?: string; body?: unknown; params?: Record<string, string> },
  unknown,
  unknown
> = async (arg) => {
  await delay(400)
  const method = arg.method || 'GET'
  const url = typeof arg.url === 'string' ? arg.url : ''
  const { path, id, action, query } = parseUrl(url)

  try {
    // POC: mock auth để demo không cần backend
    if (path === 'auth') {
      if (url.includes('login-json') && method === 'POST') {
        const body = arg.body as { username?: string; password?: string }
        const role = body?.username?.toLowerCase().includes('admin') ? 'admin' : body?.username?.toLowerCase().includes('sec') ? 'secretary' : body?.username?.toLowerCase().includes('judge') ? 'judge' : 'user'
        return {
          data: {
            access_token: 'mock-jwt-' + Date.now(),
            token_type: 'bearer',
          },
        }
      }
      if (url.includes('/me') && method === 'GET') {
        // POC: trả về role secretary để demo đủ menu Workspace, Lịch, Báo cáo
        return {
          data: {
            id: 'mock-user-1',
            username: 'demo',
            email: 'demo@uel.edu.vn',
            full_name: 'Người dùng Demo (Thư ký)',
            role: 'secretary',
            is_active: true,
          },
        }
      }
    }

    if (path === 'cases') {
      if (method === 'GET' && id) {
        const detail = mockCaseDetails[id] ?? mockCases.find((c) => c.id === id)
        if (detail) return { data: detail as CaseDetail }
        return { error: { status: 404, data: { detail: 'Không tìm thấy vụ án' } } }
      }
      if (method === 'GET') {
        let list = [...mockCases]
        if (query?.status) list = list.filter((c) => c.status === query.status)
        const withNames = list.map((c) => ({
          ...c,
          plaintiff_name: mockCaseDetails[c.id]?.plaintiff_name,
        }))
        return { data: withNames }
      }
      if (method === 'PATCH' && id && arg.body) {
        const body = arg.body as { status?: Case['status'] }
        const idx = mockCases.findIndex((c) => c.id === id)
        if (idx === -1) return { error: { status: 404, data: { detail: 'Không tìm thấy vụ án' } } }
        const updated = { ...mockCases[idx], status: body.status ?? mockCases[idx].status, updated_at: new Date().toISOString() }
        mockCases[idx] = updated
        if (mockCaseDetails[id]) mockCaseDetails[id] = { ...mockCaseDetails[id], ...updated }
        return { data: updated as CaseDetail }
      }
      if (method === 'POST' && action === 'assign') {
        const body = arg.body as { judge_id?: string }
        if (body?.judge_id) return { data: { success: true } }
        return { error: { status: 400, data: { detail: 'Thiếu judge_id' } } }
      }
      if (method === 'POST' && arg.body) {
        const b = arg.body as Record<string, unknown> & { plaintiff_name?: string; defendant_name?: string }
        const now = new Date().toISOString()
        const newId = `case-${Date.now()}`
        const newCode = `HN-GD-2026-${String(mockCases.length + 1).padStart(3, '0')}`
        const newCase: Case = {
          id: newId,
          case_code: newCode,
          track: (b.track as 'A' | 'B') || 'A',
          category: 'marriage_family',
          status: 'filed',
          claim_value_vnd: Number(b.claim_value_vnd) || 0,
          plaintiff_id: String(b.plaintiff_id),
          defendant_id: String(b.defendant_id),
          plaintiff_name: b.plaintiff_name ?? 'Nguyên đơn',
          defendant_name: b.defendant_name ?? 'Bị đơn',
          filed_at: now,
          created_at: now,
          updated_at: now,
          created_by: String(b.plaintiff_id),
        }
        mockCases.push(newCase)
        const initialEvent: CaseEvent = {
          id: `ev-${newId}`,
          type: 'filed',
          title: 'Đơn đã nộp',
          at: now,
          description: 'Đơn khởi kiện đã được Tòa án tiếp nhận qua Cổng dịch vụ công. Đang chờ Thư ký kiểm tra hình thức và thông báo kết quả.',
        }
        const detail: CaseDetail = {
          ...newCase,
          plaintiff_name: newCase.plaintiff_name,
          defendant_name: newCase.defendant_name,
          timeline: [initialEvent],
        }
        mockCaseDetails[newId] = detail
        return { data: newCase }
      }
    }

    if (path === 'filings') {
      if (method === 'GET' && id && !query?.case_id) {
        const one = mockFilings.find((f) => f.id === id)
        return one ? { data: one } : { error: { status: 404, data: { detail: 'Không tìm thấy đơn' } } }
      }
      if (method === 'GET') {
        const caseId = query?.case_id
        const list = caseId ? mockFilings.filter((f) => f.case_id === caseId) : [...mockFilings]
        return { data: list }
      }
      if (method === 'POST') {
        const b = arg.body as { case_id: string; type: string; form_data?: Record<string, unknown> }
        const newFiling = {
          id: `filing-${Date.now()}`,
          case_id: b.case_id,
          type: (b.type as Filing['type']) || 'complaint',
          filer_id: 'current-user',
          status: 'draft' as const,
          form_data: b.form_data || {},
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }
        return { data: newFiling }
      }
    }

    if (path === 'documents') {
      if (method === 'GET') {
        const caseId = query?.case_id
        const list = caseId ? mockDocuments.filter((d) => d.case_id === caseId) : [...mockDocuments]
        return { data: list }
      }
      if (method === 'POST' && (url.includes('draft') || (arg.body as Record<string, unknown>)?.doc_type)) {
        const b = arg.body as { case_id: string; doc_type: string }
        const draft = {
          id: `doc-${Date.now()}`,
          case_id: b.case_id,
          type: (b.doc_type as Document['type']) || 'notice_acceptance',
          title: 'Dự thảo văn bản',
          content: 'Nội dung dự thảo (AI điền sẵn)...',
          status: 'draft' as const,
          author_id: 'current-user',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }
        return { data: draft }
      }
    }

    if (path === 'court-sessions') {
      if (method === 'GET') {
        const caseId = query?.case_id
        let list = caseId ? mockSessions.filter((s) => s.case_id === caseId) : [...mockSessions]
        return { data: list }
      }
    }

    if (path === 'alerts') {
      if (method === 'GET') return { data: [...mockAlerts] }
    }

    if (path === 'judges') {
      if (method === 'GET') return { data: mockJudges }
    }

    return { error: { status: 404, data: { detail: 'Mock: endpoint chưa định nghĩa' } } }
  } catch (e) {
    return { error: { status: 500, data: { detail: String(e) } } }
  }
}
